import { IconAlertCircle, IconAlertTriangle, IconInfoCircle, IconX } from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import type { MaintenanceBannerType } from '@/api/hooks/maintenanceBannerQueryActions';
import {
  resolveBannerMessage,
  useMaintenanceBanner,
} from '@/api/hooks/maintenanceBannerQueryActions';

import { maintenanceBannerMessages } from './messages';

const DISMISS_STORAGE_PREFIX = 'maintenance-banner-dismissed:';

const typeStyles: Record<MaintenanceBannerType, { container: string; icon: ReactNode }> = {
  Info: {
    container:
      'bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-950 dark:text-blue-100 dark:border-blue-900',
    icon: <IconInfoCircle aria-hidden size={20} className='shrink-0' />,
  },
  Warning: {
    container:
      'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950 dark:text-amber-100 dark:border-amber-900',
    icon: <IconAlertTriangle aria-hidden size={20} className='shrink-0' />,
  },
  Critical: {
    container:
      'bg-red-50 text-red-900 border-red-200 dark:bg-red-950 dark:text-red-100 dark:border-red-900',
    icon: <IconAlertCircle aria-hidden size={20} className='shrink-0' />,
  },
};

/**
 * EWM-607 / EWM-758: top-of-screen maintenance banner. The message is shown in the
 * active UI language (EWM-758: per-language DE/EN config, with fallback). The
 * visible-banner identity is the (type + resolved message) tuple — when admins
 * change either, the sessionStorage dismiss flag for the old content no longer
 * matches and users see the new message.
 */
export const MaintenanceBanner = () => {
  const intl = useIntl();
  const { data } = useMaintenanceBanner();
  const message = data ? resolveBannerMessage(data, intl.locale) : '';
  const dismissKey =
    data?.active && message ? `${DISMISS_STORAGE_PREFIX}${data.type}:${message}` : null;

  const [dismissed, setDismissed] = useState<boolean>(() =>
    dismissKey ? sessionStorage.getItem(dismissKey) === '1' : false,
  );

  useEffect(() => {
    if (!dismissKey) {
      setDismissed(false);
      return;
    }
    setDismissed(sessionStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);

  if (!data?.active || dismissed || !dismissKey) {
    return null;
  }

  const styles = typeStyles[data.type] ?? typeStyles.Info;

  const handleDismiss = () => {
    sessionStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <div
      role='status'
      className={`flex items-center gap-3 border-b px-4 py-2 text-sm ${styles.container}`}
    >
      {styles.icon}
      <span className='flex-1 break-words'>{message}</span>
      <button
        type='button'
        onClick={handleDismiss}
        aria-label={intl.formatMessage(maintenanceBannerMessages.dismiss)}
        className='shrink-0 rounded p-1 hover:bg-black/10 dark:hover:bg-white/10'
      >
        <IconX aria-hidden size={16} />
      </button>
    </div>
  );
};
